import { useFieldArray, useFormContext } from 'react-hook-form';

import { ActiveDistrictSchool } from 'features/TalentMarketplace/TalentFiltersSidebar.types';
import { Checkbox, TableCell, TableRow, useTheme } from '@mui/material';
import { notFoundIndex } from './SchoolTable';
import { useQueryGetSchoolGroupsByDistrict } from '../queries';
import auth from 'utils/auth';
import { VacancyGroupParentRowProps } from 'features/VacanciesModal/types';
import { ATSVacanciesModalTestIds } from 'data-testids/ATS';
import { SCHOOLROLES_FORM_ID } from 'features/VacanciesModal/data';

export const VacancyGroupParentRow: React.FC<VacancyGroupParentRowProps> = ({
  group,
  numberOfColumns,
}) => {
  const theme = useTheme();
  const districtId = auth.getUser()?.district?.id;

  const { data: schoolGroups } = useQueryGetSchoolGroupsByDistrict(districtId);
  const { watch, control } = useFormContext();
  const { append, remove } = useFieldArray({ control, name: SCHOOLROLES_FORM_ID });

  const schoolRoles = watch(SCHOOLROLES_FORM_ID) || [];
  const everySchoolIsChecked = group.schools.every((school: ActiveDistrictSchool) =>
    schoolRoles.some((item) => item?.school_id === school?.id)
  );
  const someSchoolIsChecked = group.schools.some((school: ActiveDistrictSchool) =>
    schoolRoles.some((item) => item?.school_id === school?.id)
  );

  const getSchoolGroupName = (groupId: number) => {
    if (typeof groupId === 'string') {
      return 'No group';
    }
    return schoolGroups?.find((group) => group.id === groupId)?.name;
  };

  const toggleSchoolGroup = (schoolGroup: ActiveDistrictSchool[]) => {
    const schoolRoles = watch(SCHOOLROLES_FORM_ID) || [];
    const allSelected = schoolGroup.every((school) =>
      schoolRoles.some((role) => role.school_id === school.id)
    );

    if (allSelected) {
      const indicesToRemove = schoolGroup
        .map((school) => schoolRoles.findIndex((role) => role.school_id === school.id))
        .filter((index) => index !== notFoundIndex)
        .sort((a, b) => b - a);
      indicesToRemove.forEach((index) => remove(index));
    } else {
      const schoolsToAdd = schoolGroup.filter(
        (school) => !schoolRoles.some((role) => role.school_id === school.id)
      );
      schoolsToAdd.forEach((school) =>
        append({
          school_id: school.id,
          name: school.name,
          ftes_total: 1,
          visible_to_school_admin: true,
        })
      );
    }
  };

  const isDisabled = auth.isAtsLiteDistrict();

  return (
    <TableRow
      data-testid={ATSVacanciesModalTestIds.VACANCY_GROUP_PARENT_ROW}
      sx={{
        backgroundColor: someSchoolIsChecked
          ? theme.palette.primary.fill
          : isDisabled && schoolRoles.length === 1
            ? theme.palette.gray.lightest
            : 'white',
      }}
    >
      <TableCell>
        <Checkbox
          checked={everySchoolIsChecked}
          indeterminate={!everySchoolIsChecked && someSchoolIsChecked}
          onChange={() => toggleSchoolGroup(group.schools)}
          sx={{ padding: 0 }}
          data-testid={ATSVacanciesModalTestIds.GROUP_PARENT_ROW_CHECKBOX}
          disabled={isDisabled}
        />
      </TableCell>
      <TableCell data-testid={ATSVacanciesModalTestIds.GROUP_NAME} colSpan={numberOfColumns - 1}>
        {getSchoolGroupName(group.groupId)}
      </TableCell>
    </TableRow>
  );
};
